import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'

type OrderInfo = {
  service_name: string
  created_at: string
}

const STARS = [1, 2, 3, 4, 5]

const OrderFeedbackPage: React.FC = () => {
  const { id: orderId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { profile } = useAuth()

  // Paramètres transmis par le lien email (s=service, d=date).
  // Permettent d'afficher la page sans requête DB, même sans session active.
  const serviceFromUrl = searchParams.get('s')
  const dateFromUrl = searchParams.get('d')

  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(
    serviceFromUrl ? { service_name: serviceFromUrl, created_at: dateFromUrl ?? '' } : null
  )
  const [loadingOrder, setLoadingOrder] = useState(!serviceFromUrl)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)

  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [comment, setComment] = useState('')
  const [reviewerName, setReviewerName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orderId) return

    const load = async () => {
      // Si le service n'est pas dans l'URL, le charger via RPC (bypasse RLS)
      if (!serviceFromUrl) {
        setLoadingOrder(true)
        const { data: orderData } = await supabase
          .rpc('get_order_for_feedback', { p_order_id: orderId })

        if (orderData) {
          setOrderInfo({
            service_name: orderData.service_name ?? 'Cordage',
            created_at: orderData.created_at,
          })
        }
        setLoadingOrder(false)
      }

      // Vérifier si un feedback existe déjà (fonctionne en anon, retourne vide si pas de policy SELECT)
      const { data: existing } = await supabase
        .from('order_feedbacks')
        .select('id')
        .eq('order_id', orderId)
        .maybeSingle()

      if (existing) setAlreadySubmitted(true)
    }

    void load()
  }, [orderId])

  // Pré-remplir le nom depuis le profil connecté
  useEffect(() => {
    if (profile?.full_name && !reviewerName) {
      setReviewerName(profile.full_name)
    }
  }, [profile])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orderId || rating === 0 || !reviewerName.trim()) return

    setSubmitting(true)
    setError(null)

    const { error: insertError } = await supabase.from('order_feedbacks').insert({
      order_id: orderId,
      rating,
      comment: comment.trim() || null,
      reviewer_name: reviewerName.trim(),
    })

    setSubmitting(false)

    if (insertError) {
      if (insertError.code === '23505') {
        setAlreadySubmitted(true)
      } else {
        setError("Une erreur s'est produite. Merci de réessayer.")
      }
      return
    }

    setSubmitted(true)
  }

  // Si la date vient de l'URL (déjà formatée), l'utiliser telle quelle ; sinon parser l'ISO
  const orderDate = orderInfo
    ? (dateFromUrl || new Date(orderInfo.created_at).toLocaleDateString('fr-CH', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }))
    : ''

  const cardStyle: React.CSSProperties = {
    maxWidth: 520,
    margin: '2rem auto',
    background: '#0f172a',
    color: '#f1f5f9',
    borderRadius: '1rem',
    padding: '2rem',
    boxShadow: '0 0 20px rgba(0,0,0,0.4)',
  }

  if (loadingOrder) {
    return (
      <div style={cardStyle}>
        <p style={{ textAlign: 'center', color: '#94a3b8' }}>Chargement…</p>
      </div>
    )
  }

  if (!orderInfo) {
    return (
      <div style={cardStyle}>
        <h1 style={{ fontSize: '1.2rem', color: '#f87171' }}>Commande introuvable</h1>
        <p style={{ color: '#94a3b8' }}>Ce lien de feedback n'est pas valide.</p>
      </div>
    )
  }

  if (alreadySubmitted) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
        <h1 style={{ fontSize: '1.3rem', color: '#22c55e', marginBottom: '.5rem' }}>
          Merci pour votre avis !
        </h1>
        <p style={{ color: '#94a3b8' }}>Un feedback a déjà été soumis pour cette commande.</p>
      </div>
    )
  }

  if (submitted) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
        <h1 style={{ fontSize: '1.3rem', color: '#22c55e', marginBottom: '.5rem' }}>
          Merci pour votre avis !
        </h1>
        <p style={{ color: '#94a3b8' }}>
          Votre retour nous aide à améliorer notre service de cordage.
        </p>
        <p style={{ marginTop: '.5rem', color: '#64748b', fontSize: '.9rem' }}>
          À bientôt sur stringnow.ch 🎾
        </p>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h1 style={{ fontSize: '1.4rem', marginBottom: '.25rem', textAlign: 'center' }}>
        Votre avis
      </h1>
      <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '.9rem', marginBottom: '1.5rem' }}>
        {orderInfo.service_name} · {orderDate}
      </p>

      <form onSubmit={handleSubmit}>
        {/* Note en étoiles */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '.5rem' }}>
            Note *
          </label>
          <div
            style={{ display: 'flex', gap: '.25rem', fontSize: '2.5rem', cursor: 'pointer' }}
            onMouseLeave={() => setHovered(0)}
          >
            {STARS.map((star) => (
              <span
                key={star}
                data-testid={`feedback-star-${star}`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                style={{
                  color: star <= (hovered || rating) ? '#f59e0b' : '#334155',
                  transition: 'color 0.1s',
                  userSelect: 'none',
                }}
              >
                ★
              </span>
            ))}
          </div>
          {rating === 0 && (
            <p style={{ fontSize: '.8rem', color: '#94a3b8', marginTop: '.25rem' }}>
              Cliquez sur une étoile pour noter
            </p>
          )}
        </div>

        {/* Nom */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '.25rem' }}>
            Votre nom *
          </label>
          <input
            data-testid="feedback-name"
            className="input"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            placeholder="Prénom Nom"
            required
          />
        </div>

        {/* Commentaire */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '.25rem' }}>
            Commentaire
          </label>
          <textarea
            data-testid="feedback-comment"
            className="input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="Qualité du cordage, délai, accueil…"
            style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {error && (
          <p style={{ color: '#f87171', marginBottom: '1rem', fontSize: '.9rem' }}>{error}</p>
        )}

        <button
          data-testid="feedback-submit"
          className="btn btn-primary"
          type="submit"
          disabled={submitting || rating === 0 || !reviewerName.trim()}
          style={{ width: '100%' }}
        >
          {submitting ? 'Envoi…' : 'Envoyer mon avis'}
        </button>
      </form>
    </div>
  )
}

export default OrderFeedbackPage
